import { serializeAttachmentUpload, registerAttachmentUpload } from '@services/attachmentChanges/attachmentUploadOrigins';
import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { IBaseAction } from '@models/actions/IBaseAction';
import { IAttachesCreateModel, IAttachesProperty } from '@models/attaches/IAttachesCreateModel';
import { IAttachViewSignModel, IAttachWithContentBase64 } from '@models/attaches/IAttachViewSignModel';
import { IDocAttachesVersions } from '@models/attaches/IDocAttachesVersions';
import { IActionExecutor } from '@organisms/actions/IActionExecutor';
import { FilesService } from '@services/FilesService';
import { IDocBasedService } from './IDocBasedService';
import { IAttachListItemModel } from '@models/attaches/IAttachListItemModel';
import { IActionService } from '@services/actions/IActionService';
import { getActionExecutor } from '@organisms/actions/actionExecutorRegistry';
import { ISelectedFiles } from '@molecules/AttachFilesBlock/ISelectedFiles';
import { createUploadProgressHandler, UploadProgressHandler } from '@utils/uploadProgress';

export interface IAttachPropertyField {
    key: string;
    fieldType: string;
}

export class AttachActionsService implements IActionService<IBaseAction>, IDocBasedService {
    documentId: string;
    flowKey: string;

    constructor(docId: string, flowKey: string) {
        this.documentId = docId;
        this.flowKey = flowKey;
    }

    public getActionExecutor(act: IBaseAction): IActionExecutor {
        return getActionExecutor('attach', act)!;
    }

    //Получает перечень доступных действий для документа и аттача
    async fetchActions(fileId: string): Promise<AxiosResponse<IBaseAction[]>> {
        return $api.get<IBaseAction[]>(`attachment/getAttacheActions/${this.documentId}/${fileId}`);
    }

    //Получает перечень доступных групповых действий для аттачей документа
    async fetchGroupActions(fileIds: string): Promise<AxiosResponse<IBaseAction[]>> {
        return $api.get<IBaseAction[]>(`attachment/getAttacheGroupActions/${this.documentId}/${fileIds}`);
    }

    //Удаляет аттач
    async deleteAttach(attachId: string): Promise<AxiosResponse> {
        return $api.delete(`attachment/${this.documentId}/${attachId}`);
    }

    //загрузка аттачей из модального окна
    async createAttaches(
        files: ISelectedFiles,
        onUploadProgress?: UploadProgressHandler,
    ): Promise<AxiosResponse<IAttachesCreateModel>> {
        const formData = new FormData();
        files.attachProperties.map((prop) => formData.append('attachProps', serializeAttachmentUpload(prop)));
        files.files.map((f) => formData.append('attachFiles', f));

        return $api.post(`attachment/createAttaches/${this.documentId}`, formData, {
            onUploadProgress: createUploadProgressHandler(files.files, onUploadProgress),
        });
    }

    //Создает версию аттача
    async createAttachVersion(file?: File, attachId?: string): Promise<AxiosResponse> {
        const formData = new FormData();
        if (file) formData.append('attachVersion', file);
        formData.append('originOperationId', registerAttachmentUpload());
        return $api.post(`attachment/createVersion/${this.documentId}/${attachId}`, formData);
    }

    //Получает свойства/метаданные аттача
    async getAttachProperties(
        properties: IAttachPropertyField[],
        attachId?: string,
    ): Promise<AxiosResponse<{ [index: string]: IAttachesProperty }>> {
        return $api.post<{ [index: string]: IAttachesProperty }>('attachment/properties', {
            docId: this.documentId,
            attachId: attachId,
            properties,
        });
    }

    //Получает свойства/метаданные аттача
    async getAttachmentsProperties(
        properties: IAttachPropertyField[],
    ): Promise<AxiosResponse<{ [index: string]: { [index: string]: IAttachesProperty } }>> {
        return $api.post<{ [index: string]: { [index: string]: IAttachesProperty } }>(
            `attachment/properties/${this.documentId}`,
            {
                docId: this.documentId,
                properties,
            },
        );
    }

    //Для надора аттачей получает перечень readOnly свойств
    async getAttachesReadOnlyProperties(
        properties: IAttachPropertyField[],
        attachIds: string[],
    ): Promise<AxiosResponse<string[]>> {
        return $api.post<string[]>(`attachment/readonlyProperties`, {
            docId: this.documentId,
            attachIds: attachIds,
            properties,
        });
    }

    //Редактирует аттач
    async editAttach(
        attachProperties: any,
        attachId: string | undefined,
        properties: IAttachPropertyField[],
    ): Promise<AxiosResponse> {
        return $api.post(
            `attachment/edit/${this.documentId}/${attachId}`,
            properties
                .filter((property) => Object.prototype.hasOwnProperty.call(attachProperties, property.key))
                .map((property) => ({
                    key: property.key,
                    fieldType: property.fieldType,
                    value: attachProperties[property.key],
                })),
        );
    }

    //получает модель таблицы для прикрепления аттачей
    async getAttachInfo(actionKey: string, activityId?: string): Promise<AxiosResponse<IAttachesCreateModel>> {
        return $api.get<IAttachesCreateModel>(
            `attachment/getAttachCreationModel/${this.documentId}/${this.flowKey}/${actionKey}/${activityId ?? ''}`,
        );
    }

    //Возвращает список аттачей для документа (по ид активити)
    public async getDocAttachKeysByActKey(
        actId: string,
        actionKey?: string,
    ): Promise<AxiosResponse<IDocAttachesVersions[]>> {
        return $api.get<IDocAttachesVersions[]>(`/attachment/getDocAttachList/${actId}/${actionKey}`);
    }

    //Возвращает содержимое аттача в base64
    public async getAttachContentInBase64(docId: number, attachId: string): Promise<string> {
        let response = await $api.get<string>(`/attachment/base64Content/${docId}/${attachId}`);
        return response.data;
    }

    //Возвращает все подписи для аттача
    public getAttachSigns(attachId: number): Promise<AxiosResponse<IAttachViewSignModel>> {
        return $api.get<IAttachViewSignModel>(`/attachment/getSigns/${this.documentId}/${attachId}`);
    }

    //Скачать подпись
    public downloadAttachSigns(attachId: number, signId: string): void {
        FilesService.downloadFile(`attachment/downloadSign/${this.documentId}/${attachId}/${signId}`);
    }

    //скачать подпись и содержимое файла в base64 (для проверки)
    public getAttachSignsWithContentBase64(
        _docId: string,
        attachId: number,
        signId: string,
    ): Promise<AxiosResponse<IAttachWithContentBase64>> {
        return $api.get<IAttachWithContentBase64>(
            `/attachment/getSignWithContent/${this.documentId}/${attachId}/${signId}`,
        );
    }

    public getDocsAttaches(docIds: number[]): Promise<AxiosResponse<IAttachListItemModel[]>> {
        return $api.get<IAttachListItemModel[]>('/attachment/getDocsAttaches', { params: { docIds: docIds } });
    }
}
